import React, { useState, useEffect } from 'react';
import MemberManagement from './pages/MemberManagement';
import TeamManagement from './pages/TeamManagement';
import MemberDetail from './pages/MemberDetail';
import ConsumableManagement from './pages/ConsumableManagement';
import ReagentManagement from './pages/ReagentManagement';
import InstrumentManagement from './pages/InstrumentManagement';
import SharedPlatformPage from './pages/instruments/SharedPlatformPage';
import UserProfile from './pages/UserProfile';
import SuperadminTeams from './pages/superadmin/SuperadminTeams';
import SuperadminUnassignedUsers from './pages/superadmin/SuperadminUnassignedUsers';
import PlatformAdminLogin from './platform-admin/PlatformAdminLogin';
import PlatformAdminLayout from './platform-admin/PlatformAdminLayout';
import { STAFF_CONSOLE_LOGIN_PATH } from './platform-admin/staffConsolePaths';
import MerchantLogin from './merchant/MerchantLogin';
import MerchantRegister from './merchant/MerchantRegister';
import MerchantLayout from './merchant/MerchantLayout';
import MerchantOrders from './merchant/MerchantOrders';
import MerchantShopCenter from './merchant/MerchantShopCenter';
import MerchantCommitmentTemplate from './merchant/MerchantCommitmentTemplate';
import MerchantBilling from './merchant/MerchantBilling';
import MerchantPromo from './merchant/MerchantPromo';
import MerchantBrandDiscounts from './merchant/MerchantBrandDiscounts';
import MerchantAfterSales from './merchant/MerchantAfterSales';
import SuperadminMerchants from './pages/superadmin/SuperadminMerchants';
import MainLoginPage from './pages/MainLoginPage';
import ScrollStorySitePage from './pages/marketing/ScrollStorySitePage';
import Dashboard from './pages/Dashboard';
import ExperimentRecords from './pages/workbench/ExperimentRecords';
import ExperimentRecordDetail from './pages/workbench/ExperimentRecordDetail';
import ExperimentRecordForm from './pages/workbench/ExperimentRecordForm';
import SharedExperimentNotebooks from './pages/workbench/SharedExperimentNotebooks';
import SharedExperimentNotebookDetail from './pages/workbench/SharedExperimentNotebookDetail';
import WorkReports from './pages/workbench/WorkReports';
import ApprovalCenter from './pages/workbench/ApprovalCenter';
import ApplicationSubmission from './pages/workbench/ApplicationSubmission';
import ApprovalManagement from './pages/workbench/ApprovalManagement';
import ApprovalApproverSettings from './pages/workbench/ApprovalApproverSettings';
import TeamBilling from './pages/workbench/TeamBilling';
import WorkHours from './pages/workbench/WorkHours';
import CreateActivity from './pages/lab-activities/CreateActivity';
import DutySchedule from './pages/lab-activities/DutySchedule';
import LiteratureList from './pages/literature/LiteratureList';
import LiteratureInspectRedirect from './pages/literature/LiteratureInspectRedirect';
import LiteratureForm from './pages/literature/LiteratureForm';
import PDFViewer from './pages/literature/PDFViewer';
import Notebook from './pages/literature/Notebook';
import SharedFolders from './pages/literature/SharedFolders';
import SharedFolderDetail from './pages/literature/SharedFolderDetail';
import KnowledgeBaseList from './pages/knowledgeBase/KnowledgeBaseList';
import KnowledgeBaseDetail from './pages/knowledgeBase/KnowledgeBaseDetail';
import KnowledgeBaseForm from './pages/knowledgeBase/KnowledgeBaseForm';
import FeedbackSquare from './pages/feedback/FeedbackSquare';
import FeedbackPostForm from './pages/feedback/FeedbackPostForm';
import FeedbackPostDetail from './pages/feedback/FeedbackPostDetail';
import MyFeedbackRecords from './pages/feedback/MyFeedbackRecords';
import FeedbackAdminReview from './pages/feedback/FeedbackAdminReview';
import FeedbackAnalyticsDashboard from './pages/feedback/FeedbackAnalyticsDashboard';
import FeedbackSettingsPage from './pages/feedback/FeedbackSettingsPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Sidebar from './components/Sidebar';
import TopNavbar from './components/TopNavbar';
import AuthGuard from './components/AuthGuard';
import TenantShellGuard from './components/TenantShellGuard';
import FirstLoginPasswordModal from './components/FirstLoginPasswordModal';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

const AppContent: React.FC = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { token, isFirstLogin, passwordChanged } = useAuth();
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 480;
      setIsMobile(mobile);
      // 移动端默认收起侧边栏
      if (mobile) {
        setSidebarCollapsed(true);
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 检查是否需要显示首次登录密码修改弹窗
  React.useEffect(() => {
    if (token && isFirstLogin && !passwordChanged) {
      setShowFirstLoginModal(true);
    }
  }, [token, isFirstLogin, passwordChanged]);

  const handleFirstLoginSuccess = () => {
    setShowFirstLoginModal(false);
    // 刷新页面以更新用户状态
    window.location.reload();
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // 创建需要认证的页面布局
  const createAuthenticatedLayout = (Component: React.ComponentType) => {
    return (
      <TenantShellGuard>
        <div className="app-layout">
          <Sidebar isCollapsed={sidebarCollapsed} onToggle={toggleSidebar} />
          {/* 移动端遮罩层 */}
          {isMobile && !sidebarCollapsed && (
            <div 
              className="sidebar-overlay"
              onClick={toggleSidebar}
            />
          )}
          <div className={`main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
            <TopNavbar isCollapsed={sidebarCollapsed} onToggleSidebar={toggleSidebar} />
            <div className="page-container">
              <Component />
            </div>
          </div>
        </div>
      </TenantShellGuard>
    );
  };

  return (
    <Router>
      <Routes>
        {/* 系统默认入口：登录页；产品官网 /site（滚动叙事） */}
        <Route path="/" element={<MainLoginPage />} />
        <Route path="/site" element={<ScrollStorySitePage />} />
        <Route path="/site/story" element={<Navigate to="/site" replace />} />

        {/* 内部运维：登录页不挂在对外首页，请直接访问独立路径 */}
        <Route path={STAFF_CONSOLE_LOGIN_PATH} element={<PlatformAdminLogin />} />
        <Route path="/platform-admin/login" element={<Navigate to={STAFF_CONSOLE_LOGIN_PATH} replace />} />
        <Route path="/platform-admin" element={<PlatformAdminLayout />}>
          <Route index element={<Navigate to="teams" replace />} />
          <Route path="unassigned-users" element={<SuperadminUnassignedUsers />} />
          <Route path="teams" element={<SuperadminTeams />} />
          <Route path="merchants" element={<SuperadminMerchants />} />
          <Route path="permissions" element={<Navigate to="/platform-admin/teams" replace />} />
          <Route path="feedback/review" element={<FeedbackAdminReview />} />
          <Route path="feedback/analytics" element={<FeedbackAnalyticsDashboard />} />
          <Route path="feedback/settings" element={<FeedbackSettingsPage />} />
        </Route>

        {/* 商家端 */}
        <Route path="/merchant/login" element={<MerchantLogin />} />
        <Route path="/merchant/register/:token" element={<MerchantRegister />} />
        <Route path="/merchant/commitment-template" element={<MerchantCommitmentTemplate />} />
        <Route path="/merchant" element={<MerchantLayout />}>
          <Route index element={<Navigate to="orders" replace />} />
          <Route path="orders" element={<MerchantOrders />} />
          <Route path="orders/:id" element={<MerchantOrders />} />
          <Route path="billing" element={<MerchantBilling />} />
          <Route path="brand-discounts" element={<MerchantBrandDiscounts />} />
          <Route path="after-sales" element={<MerchantAfterSales />} />
          <Route path="promo" element={<MerchantPromo />} />
          <Route path="shop" element={<MerchantShopCenter />} />
        </Route>

        {/* 旧超级管理路由重定向 */}
        <Route path="/superadmin/teams" element={<Navigate to="/platform-admin/teams" replace />} />
        <Route path="/superadmin/permissions" element={<Navigate to="/platform-admin/teams" replace />} />
        
        {/* 忘记密码和重置密码路由 - 不需要导航栏 */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        
        {/* 系统内部路由 - 需要左右布局 */}
        <Route path="/dashboard" element={createAuthenticatedLayout(Dashboard)} />
        {/* 快速工作台改为首页弹窗展示，故不再单独提供路由 */}
        
        {/* 其他系统页面路由 */}
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route path="/members" element={<AuthGuard redirectTo="/members">{createAuthenticatedLayout(MemberManagement)}</AuthGuard>} />
        <Route path="/members/:id" element={<AuthGuard redirectTo="/members">{createAuthenticatedLayout(MemberDetail)}</AuthGuard>} />
        <Route path="/team" element={<AuthGuard redirectTo="/team">{createAuthenticatedLayout(TeamManagement)}</AuthGuard>} />
        <Route path="/consumables" element={createAuthenticatedLayout(ConsumableManagement)} />
        <Route path="/reagents" element={createAuthenticatedLayout(ReagentManagement)} />
        <Route path="/instruments/stock" element={createAuthenticatedLayout(InstrumentManagement)} />
        <Route
          path="/instruments/shared-platform"
          element={<AuthGuard redirectTo="/instruments/shared-platform">{createAuthenticatedLayout(SharedPlatformPage)}</AuthGuard>}
        />
        <Route path="/instruments/work-hours" element={<AuthGuard redirectTo="/instruments/work-hours">{createAuthenticatedLayout(WorkHours)}</AuthGuard>} />
        <Route path="/instruments" element={createAuthenticatedLayout(InstrumentManagement)} />
        <Route path="/finance" element={<Navigate to="/dashboard" replace />} />
        <Route path="/profile" element={<AuthGuard redirectTo="/profile">{createAuthenticatedLayout(UserProfile)}</AuthGuard>} />

        <Route
          path="/permission-management"
          element={<Navigate to="/platform-admin/teams" replace />}
        />
        
        {/* 工作台路由 — 实验记录本（具体路径须写在列表路径之前） */}
        <Route
          path="/workbench/experiment-records/shared/folders/:folderId"
          element={
            <AuthGuard redirectTo="/workbench/experiment-records/shared">
              {createAuthenticatedLayout(SharedExperimentNotebookDetail)}
            </AuthGuard>
          }
        />
        <Route
          path="/workbench/experiment-records/shared"
          element={
            <AuthGuard redirectTo="/workbench/experiment-records/shared">
              {createAuthenticatedLayout(SharedExperimentNotebooks)}
            </AuthGuard>
          }
        />
        <Route
          path="/workbench/experiment-records/notifications"
          element={<Navigate to="/workbench/approval-management?tab=todo#am-share-invites" replace />}
        />
        <Route
          path="/workbench/experiment-records/new"
          element={<AuthGuard redirectTo="/workbench/experiment-records/new">{createAuthenticatedLayout(ExperimentRecordForm)}</AuthGuard>}
        />
        <Route
          path="/workbench/experiment-records/:id/edit"
          element={<AuthGuard redirectTo="/workbench/experiment-records">{createAuthenticatedLayout(ExperimentRecordForm)}</AuthGuard>}
        />
        <Route
          path="/workbench/experiment-records/:id"
          element={<AuthGuard redirectTo="/workbench/experiment-records">{createAuthenticatedLayout(ExperimentRecordDetail)}</AuthGuard>}
        />
        <Route path="/workbench/experiment-records" element={<AuthGuard redirectTo="/workbench/experiment-records">{createAuthenticatedLayout(ExperimentRecords)}</AuthGuard>} />
        <Route path="/workbench/work-reports" element={<AuthGuard redirectTo="/workbench/work-reports">{createAuthenticatedLayout(WorkReports)}</AuthGuard>} />
        <Route path="/workbench/approval-center" element={<AuthGuard redirectTo="/workbench/approval-center">{createAuthenticatedLayout(ApprovalCenter)}</AuthGuard>} />
        <Route path="/workbench/application-submission" element={<AuthGuard redirectTo="/workbench/application-submission">{createAuthenticatedLayout(ApplicationSubmission)}</AuthGuard>} />
        <Route path="/workbench/approval-management" element={<AuthGuard redirectTo="/workbench/approval-management">{createAuthenticatedLayout(ApprovalManagement)}</AuthGuard>} />
        <Route path="/workbench/approval-approver-settings" element={<AuthGuard redirectTo="/workbench/approval-approver-settings">{createAuthenticatedLayout(ApprovalApproverSettings)}</AuthGuard>} />
        <Route path="/workbench/team-billing" element={<AuthGuard redirectTo="/workbench/team-billing">{createAuthenticatedLayout(TeamBilling)}</AuthGuard>} />
        <Route path="/workbench/student-files" element={<Navigate to="/workbench/experiment-records" replace />} />
        <Route path="/workbench/work-hours" element={<AuthGuard redirectTo="/workbench/work-hours">{createAuthenticatedLayout(WorkHours)}</AuthGuard>} />
        
        {/* 实验室活动路由 */}
        <Route path="/lab-activities/create-activity" element={<AuthGuard redirectTo="/lab-activities/create-activity">{createAuthenticatedLayout(CreateActivity)}</AuthGuard>} />
        <Route path="/lab-activities/duty-schedule" element={<AuthGuard redirectTo="/lab-activities/duty-schedule">{createAuthenticatedLayout(DutySchedule)}</AuthGuard>} />
        <Route path="/lab-activities/lab-closure" element={<Navigate to="/dashboard" replace />} />
        {/* 文献管理：具体路径须写在 /literature/:id 之前，避免被单段 id 抢匹配 */}
        <Route path="/literature" element={<AuthGuard redirectTo="/literature">{createAuthenticatedLayout(LiteratureList)}</AuthGuard>} />
        <Route path="/literature/new" element={<AuthGuard redirectTo="/literature/new">{createAuthenticatedLayout(LiteratureForm)}</AuthGuard>} />
        <Route path="/literature/category/:categoryId" element={<AuthGuard redirectTo="/literature">{createAuthenticatedLayout(LiteratureList)}</AuthGuard>} />
        <Route path="/literature/shared/folders/:folderId" element={<AuthGuard redirectTo="/literature/shared">{createAuthenticatedLayout(SharedFolderDetail)}</AuthGuard>} />
        <Route path="/literature/shared" element={<AuthGuard redirectTo="/literature/shared">{createAuthenticatedLayout(SharedFolders)}</AuthGuard>} />
        <Route path="/literature/notebook" element={<AuthGuard redirectTo="/literature/notebook">{createAuthenticatedLayout(Notebook)}</AuthGuard>} />
        <Route
          path="/literature/notifications"
          element={<Navigate to="/workbench/approval-management?tab=todo#am-share-invites" replace />}
        />
        <Route path="/literature/:id/edit" element={<AuthGuard redirectTo="/literature">{createAuthenticatedLayout(LiteratureForm)}</AuthGuard>} />
        <Route path="/literature/:id/view" element={<AuthGuard redirectTo="/literature">{createAuthenticatedLayout(PDFViewer)}</AuthGuard>} />
        <Route path="/literature/:id" element={<AuthGuard redirectTo="/literature">{createAuthenticatedLayout(LiteratureInspectRedirect)}</AuthGuard>} />
        
        {/* 知识库路由 */}
        <Route path="/knowledge-base" element={<AuthGuard redirectTo="/knowledge-base">{createAuthenticatedLayout(KnowledgeBaseList)}</AuthGuard>} />
        <Route path="/knowledge-base/new" element={<AuthGuard redirectTo="/knowledge-base/new">{createAuthenticatedLayout(KnowledgeBaseForm)}</AuthGuard>} />
        <Route path="/knowledge-base/:id" element={<AuthGuard redirectTo="/knowledge-base">{createAuthenticatedLayout(KnowledgeBaseDetail)}</AuthGuard>} />
        <Route path="/knowledge-base/:id/edit" element={<AuthGuard redirectTo="/knowledge-base">{createAuthenticatedLayout(KnowledgeBaseForm)}</AuthGuard>} />

        {/* 匿名反馈模块路由 */}
        <Route path="/feedback/square" element={<AuthGuard redirectTo="/feedback/square">{createAuthenticatedLayout(FeedbackSquare)}</AuthGuard>} />
        <Route path="/feedback/post/new" element={<AuthGuard redirectTo="/feedback/post/new">{createAuthenticatedLayout(FeedbackPostForm)}</AuthGuard>} />
        <Route path="/feedback/post/:id" element={<AuthGuard redirectTo="/feedback/square">{createAuthenticatedLayout(FeedbackPostDetail)}</AuthGuard>} />
        <Route path="/feedback/my" element={<AuthGuard redirectTo="/feedback/my">{createAuthenticatedLayout(MyFeedbackRecords)}</AuthGuard>} />
        <Route path="/admin/feedback/review" element={<AuthGuard redirectTo="/admin/feedback/review">{createAuthenticatedLayout(FeedbackAdminReview)}</AuthGuard>} />
        <Route path="/admin/feedback/analytics" element={<AuthGuard redirectTo="/admin/feedback/analytics">{createAuthenticatedLayout(FeedbackAnalyticsDashboard)}</AuthGuard>} />
        <Route path="/admin/feedback/settings" element={<AuthGuard redirectTo="/admin/feedback/settings">{createAuthenticatedLayout(FeedbackSettingsPage)}</AuthGuard>} />
      </Routes>
      
      {/* 首次登录密码修改弹窗 */}
      <FirstLoginPasswordModal 
        isOpen={showFirstLoginModal}
        onSuccess={handleFirstLoginSuccess}
      />
    </Router>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <AppContent />
  </AuthProvider>
);

export default App;